import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, History, Eye, DollarSign } from "lucide-react";
import { api, inputCls, labelCls, rd, askPin } from "./posShared";
import PayModal from "./PayModal";

const empty = { name: "", phone: "", whatsapp: "", email: "", doc: "", address: "", notes: "" };

export default function Clientes({ customers, reload, settings, onView, isAdmin = true }) {
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [history, setHistory] = useState(null);

  const filtered = customers.filter(
    (c) => c.name.toLowerCase().includes(q.toLowerCase()) || (c.phone || "").includes(q)
  );

  const save = async (d) => {
    try {
      if (d.id) await api.put(`/pos/customers/${d.id}`, d);
      else await api.post("/pos/customers", d);
      toast.success("Cliente guardado");
      setModal(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar a ${c.name}?`)) return;
    const pin = await askPin();
    try {
      await api.delete(`/pos/customers/${c.id}?pin=${encodeURIComponent(pin)}`);
      toast.success("Cliente eliminado");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openHistory = async (c) => {
    setHistory({ customer: c, data: null });
    try {
      const { data } = await api.get(`/pos/customers/${c.id}/history`);
      setHistory({ customer: c, data });
    } catch (e) {
      toast.error("No se pudo cargar el historial");
      setHistory(null);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl">Clientes</h1>
        <button data-testid="pos-add-customer" onClick={() => setModal({ ...empty })}
          className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition">
          <Plus size={15} /> Nuevo cliente
        </button>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o teléfono..." className={`${inputCls} pl-9`} />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((c) => (
          <div key={c.id} data-testid={`pos-customer-${c.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-espresso">{c.name}</h3>
                <p className="text-xs text-neutral-500">{c.phone}{c.doc ? ` · ${c.doc}` : ""}</p>
              </div>
              <div className="flex gap-1">
                <button data-testid={`pos-customer-history-${c.id}`} onClick={() => openHistory(c)} title="Historial de compras" className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><History size={13} /></button>
                <button onClick={() => setModal({ ...c })} className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Pencil size={13} /></button>
                {isAdmin && <button onClick={() => del(c)} className="p-1.5 border border-neutral-200 rounded-lg text-neutral-400 hover:text-red-500 hover:border-red-300"><Trash2 size={13} /></button>}
              </div>
            </div>
            {c.email && <p className="text-xs text-neutral-400 mt-2">{c.email}</p>}
            {c.address && <p className="text-xs text-neutral-400">{c.address}</p>}
            {c.notes && <p className="text-xs text-neutral-500 mt-2 italic">{c.notes}</p>}
          </div>
        ))}
        {filtered.length === 0 && <p className="text-neutral-400 col-span-full py-8 text-center">Sin clientes.</p>}
      </div>

      {modal && <CustomerModal data={modal} onClose={() => setModal(null)} onSave={save} />}
      {history && <HistoryModal state={history} onClose={() => setHistory(null)} onView={onView} onReload={() => openHistory(history.customer)} />}
    </div>
  );
}

function HistoryModal({ state, onClose, onView, onReload }) {
  const [pay, setPay] = useState(null);
  const { customer, data } = state;
  const s = data?.summary;
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
      <div data-testid="pos-history-modal" className="bg-white w-full max-w-2xl rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 sticky top-0 bg-white">
          <div>
            <h2 className="font-serif text-2xl">{customer.name}</h2>
            <p className="text-xs text-neutral-500">{customer.phone} {customer.email && `· ${customer.email}`}</p>
          </div>
          <button data-testid="pos-history-close" onClick={onClose} className="text-neutral-400 text-xl hover:text-espresso">✕</button>
        </div>
        <div className="p-6">
          {!data ? <p className="text-neutral-400">Cargando historial...</p> : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <Mini l="Total gastado" v={rd(s.total_spent)} accent />
                <Mini l="Compras" v={s.invoice_count} />
                <Mini l="Unidades" v={s.units_bought} />
                <Mini l="Pendiente" v={rd(s.total_pending)} />
              </div>
              <div className="space-y-2">
                {data.invoices.length ? data.invoices.map((inv) => (
                  <div key={inv.id} data-testid={`pos-history-inv-${inv.id}`} className="flex items-center justify-between border-b border-neutral-50 py-2 text-sm">
                    <div>
                      <div className="font-medium text-espresso">{inv.number} <span className={`ml-1 text-[10px] uppercase px-1.5 py-0.5 rounded-full ${inv.status === "pagada" ? "bg-emerald-50 text-emerald-600" : inv.status === "pendiente" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600"}`}>{inv.status}</span></div>
                      <div className="text-xs text-neutral-400">{(inv.created_at || "").slice(0, 16).replace("T", " ")} · {rd(inv.total)}{inv.balance > 0 ? ` · pendiente ${rd(inv.balance)}` : ""}</div>
                    </div>
                    <div className="flex gap-1.5">
                      <button data-testid={`pos-history-view-${inv.id}`} onClick={() => onView && onView(inv)} className="p-1.5 border border-neutral-200 rounded-lg hover:border-espresso"><Eye size={13} /></button>
                      {inv.status !== "anulada" && inv.balance > 0 && (
                        <button data-testid={`pos-history-pay-${inv.id}`} onClick={() => setPay(inv)} className="p-1.5 border border-neutral-200 rounded-lg text-emerald-600 hover:border-emerald-400"><DollarSign size={13} /></button>
                      )}
                    </div>
                  </div>
                )) : <p className="text-neutral-400 text-center py-6">Aún no tiene compras registradas.</p>}
              </div>
            </>
          )}
        </div>
      </div>
      {pay && <PayModal invoice={pay} onClose={() => setPay(null)} onPaid={() => { setPay(null); onReload(); }} />}
    </div>
  );
}

const Mini = ({ l, v, accent }) => (
  <div className="bg-neutral-50 rounded-xl p-3">
    <p className="text-[10px] uppercase tracking-wider text-neutral-500">{l}</p>
    <p className={`text-lg font-serif mt-0.5 ${accent ? "text-emerald-600" : "text-espresso"}`}>{v}</p>
  </div>
);

function CustomerModal({ data, onClose, onSave }) {
  const [d, setD] = useState(data);
  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  return (
    <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
          <h2 className="font-serif text-2xl">{d.id ? "Editar" : "Nuevo"} cliente</h2>
          <button onClick={onClose} className="text-neutral-400 text-xl">✕</button>
        </div>
        <div className="p-6 space-y-3">
          <div><label className={labelCls}>Nombre completo</label><input data-testid="pos-cf-name" value={d.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Teléfono</label><input data-testid="pos-cf-phone" value={d.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>WhatsApp</label><input value={d.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></div>
          </div>
          <div><label className={labelCls}>Correo (opcional)</label><input value={d.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Cédula o RNC (opcional)</label><input value={d.doc} onChange={(e) => set("doc", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Dirección (opcional)</label><input value={d.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></div>
          <div><label className={labelCls}>Notas</label><textarea rows={2} value={d.notes} onChange={(e) => set("notes", e.target.value)} className={inputCls} /></div>
          <button data-testid="pos-save-customer" onClick={() => onSave(d)} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">Guardar cliente</button>
        </div>
      </div>
    </div>
  );
}
